import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import i18n from "../utils/i18n";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const whitelabel_id = Number(process.env.REACT_APP_WHITELABEL_ID) || null;

// Instância principal da API com interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Api-Key": API_KEY },
});

// Instância separada para renovação de token (sem interceptors)
const apiWithoutInterceptors = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Api-Key": API_KEY },
});

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    config.headers["Accept-Language"] = i18n.language;

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

const removeLocalStorage = () => {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("isTempPassword");
};

const isInSsoFlow = () => {
  return window.location.pathname.includes("/autenticacao/genial");
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Durante o fluxo SSO da Genial, ignora 401 de requests residuais
      // do usuário anterior para não apagar o novo token do localStorage.
      if (isInSsoFlow()) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const isTempPassword = localStorage.getItem("isTempPassword");
      if (isTempPassword) return;

      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        try {
          if (originalRequest.url === "/auth/token/renew") {
            console.warn("Erro ao renovar o token. Loop detectado.");
            return Promise.reject(error);
          }

          // Usa a instância sem interceptors e envia o refreshToken no body
          const { data } = await apiWithoutInterceptors.post(
            `/auth/token/renew`,
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
                "Accept-Language": i18n.language,
              },
            }
          );

          localStorage.setItem("accessToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);

          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error("Erro ao renovar o token:", refreshError);
          removeLocalStorage();
          window.location.reload();
          return Promise.reject(refreshError);
        }
      } else {
        removeLocalStorage();
      }
    }

    return Promise.reject(error);
  }
);

export { api, apiWithoutInterceptors, whitelabel_id };
